import { useEffect, useState, useRef } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Bell, CheckCheck, CircleDot, Info, AlertTriangle, XCircle, CheckCircle2, Trash2 } from 'lucide-react';
import { fmtDate } from '@/lib/format';

const KIND_META = {
  info:    { icon: Info, cls: 'text-sky-700 bg-sky-50 border-sky-200' },
  success: { icon: CheckCircle2, cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  warning: { icon: AlertTriangle, cls: 'text-amber-700 bg-amber-50 border-amber-200' },
  error:   { icon: XCircle, cls: 'text-rose-700 bg-rose-50 border-rose-200' },
};

/** In-app notifications bell.
 * Polls unread count every 45s; opens a popover with the last 20 events.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const timerRef = useRef(null);

  const fetchCount = async () => {
    try {
      const { data } = await api.get('/notifications/unread-count');
      setUnread(data?.count || 0);
    } catch (_) { /* silent */ }
  };

  const fetchList = async () => {
    try {
      const { data } = await api.get('/notifications?limit=20');
      setItems(data?.items || []);
      setUnread(data?.unread_count || 0);
    } catch (_) { /* silent */ }
  };

  useEffect(() => {
    fetchCount();
    timerRef.current = setInterval(fetchCount, 45000);
    const onFocus = () => fetchCount();
    window.addEventListener('focus', onFocus);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  useEffect(() => { if (open) fetchList(); }, [open]);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnread(n => Math.max(0, n - 1));
    } catch (_) { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setItems(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
      toast.success('Marcadas como leídas');
    } catch (_) { /* silent */ }
  };

  const removeOne = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setItems(prev => prev.filter(n => n.id !== id));
    } catch (_) { /* silent */ }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative" data-testid="notification-bell" aria-label="Notificaciones">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center"
              data-testid="notification-bell-badge"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-popover">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-semibold text-slate-900">Notificaciones</div>
          {unread > 0 && (
            <Button variant="ghost" size="sm" onClick={markAllRead} data-testid="notifications-mark-all-read">
              <CheckCheck className="h-3.5 w-3.5 mr-1" /> Marcar todas
            </Button>
          )}
        </div>
        <div className="max-h-[70vh] overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500" data-testid="notifications-empty">
              <Bell className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              Sin notificaciones
            </div>
          ) : (
            <ul className="divide-y">
              {items.map(n => {
                const meta = KIND_META[n.kind] || KIND_META.info;
                const Icon = meta.icon;
                return (
                  <li
                    key={n.id}
                    className={`p-3 hover:bg-slate-50 flex gap-3 ${!n.read ? 'bg-sky-50/40' : ''}`}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className={`h-8 w-8 rounded-full border flex items-center justify-center shrink-0 ${meta.cls}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <div className="font-medium text-slate-900 text-sm truncate">{n.title}</div>
                        {!n.read && <CircleDot className="h-3 w-3 text-sky-500 shrink-0" />}
                      </div>
                      <div className="text-xs text-slate-600 mt-0.5">{n.message}</div>
                      <div className="text-[10px] text-slate-400 mt-1">{fmtDate(n.created_at)}</div>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      {!n.read && (
                        <button onClick={() => markRead(n.id)} className="text-[11px] text-sky-600 hover:text-sky-800" title="Marcar leída">
                          <CheckCheck className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button onClick={() => removeOne(n.id)} className="text-[11px] text-slate-400 hover:text-rose-600" title="Descartar">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
